/**
 * @fileoverview Simple session logging system for CasebookMap.
 * Collects timestamped log messages with severity levels.
 * @module helpers/LogHelper
 */

import { escapeHtml } from '../utils/jrfuncs.js';


// ==========================================
// SEVERITY CONSTANTS
// ==========================================

const LOG_SEVERITY = {
    DEBUG: 'debug',
    INFO: 'info',
    WARNING: 'warning',
    ERROR: 'error',
};


/**
 * Simple session logger that collects timestamped messages with severity levels.
 * Messages are stored in memory for the duration of the session.
 * @class
 */
export class LogHelper {
    /**
     * Creates a new LogHelper instance.
     * @param {Object} mapApp - The MapApp instance
     */
    constructor(mapApp) {
        this.mapApp = mapApp;
        this.logMessages = [];
    }


    // ==========================================
    // PUBLIC LOGGING METHODS
    // ==========================================

    /**
     * Adds a log message with the specified severity.
     * @param {string} message - The log message text
     * @param {string} [severity=LOG_SEVERITY.INFO] - Severity level (debug, info, warning, error)
     */
    addMessage(message, severity = LOG_SEVERITY.INFO) {
        this.logMessages.push({
            timestamp: new Date(),
            message: message,
            severity: severity,
        });
    }

    /**
     * Adds an error-level log message.
     * @param {string} message - The error message text
     */
    addError(message) {
        this.addMessage(message, LOG_SEVERITY.ERROR);
    }

    /**
     * Adds a warning-level log message.
     * @param {string} message - The warning message text
     */
    addWarning(message) {
        this.addMessage(message, LOG_SEVERITY.WARNING);
    }

    /**
     * Adds a debug-level log message.
     * @param {string} message - The debug message text
     */
    addDebug(message) {
        this.addMessage(message, LOG_SEVERITY.DEBUG);
    }


    // ==========================================
    // OUTPUT
    // ==========================================

    /**
     * Returns all log messages as an HTML string, sorted with most recent first.
     * Error messages are colored red, warnings orange, debug messages gray.
     * @returns {string} HTML string of formatted log messages
     */
    getLogMessagesAsHtml() {
        if (this.logMessages.length === 0) {
            return '<span class="text-muted">No log messages.</span>';
        }

        const sorted = [...this.logMessages].reverse();

        const lines = sorted.map(entry => {
            const timeStr = entry.timestamp.toLocaleTimeString();
            const sevLabel = entry.severity.toUpperCase().padEnd(7);
            const escapedMessage = escapeHtml(entry.message);

            let color = '';
            if (entry.severity === LOG_SEVERITY.ERROR) {
                color = ' style="color: #dc3545;"';
            } else if (entry.severity === LOG_SEVERITY.WARNING) {
                color = ' style="color: #fd7e14;"';
            } else if (entry.severity === LOG_SEVERITY.DEBUG) {
                color = ' style="color: #6c757d;"';
            }

            return `<span${color}>[${timeStr}] ${sevLabel} ${escapedMessage}</span>`;
        });

        return lines.join('\n');
    }
}
